import React, {useEffect, useState} from "react"
import {Button, Card, CardBody, Col, Container, FormGroup, Input, Label, Row} from "reactstrap"
import {Table, Tbody, Td, Th, Thead, Tr} from "react-super-responsive-table"

import Breadcrumbs from "../../components/Common/Breadcrumb"
import MetaTitle from "../../components/MetaTitle"
import ProgressView from "../../components/ProgressView"
import useAuditLogs from "../../hooks/useAuditLogs"

const formatDate = (iso) => (iso ? new Date(iso).toLocaleString() : "—")

const AuditLogs = props => {
    const [filters, setFilters] = useState({action: "", target_type: "", target_id: "", actor_id: ""})
    const [draftFilters, setDraftFilters] = useState({action: "", target_type: "", target_id: "", actor_id: ""})
    const [page, setPage] = useState(1)
    const limit = 25

    const {rows, totalRows, totalPages, isLoading, loadAuditLogs} = useAuditLogs({
        action: filters.action,
        targetType: filters.target_type,
        targetId: filters.target_id,
        actorId: filters.actor_id,
        page,
        limit,
    })

    useEffect(() => {
        loadAuditLogs()
    }, [filters, page])

    const applyFilters = () => {
        setPage(1)
        setFilters(draftFilters)
    }

    const clearFilters = () => {
        const blank = {action: "", target_type: "", target_id: "", actor_id: ""}
        setDraftFilters(blank)
        setFilters(blank)
        setPage(1)
    }

    return (
        <React.Fragment>
            <div className="page-content">
                <MetaTitle title="Audit Logs"/>
                <Container fluid={true}>
                    <Breadcrumbs title="Admin" breadcrumbItem="Audit Logs" {...props}/>
                    <Row>
                        <Col xs="12">
                            <Card>
                                <CardBody>

                                    <Row className="mb-3">
                                        <Col md="3">
                                            <FormGroup>
                                                <Label>Action</Label>
                                                <Input
                                                    bsSize="sm"
                                                    value={draftFilters.action}
                                                    placeholder="e.g. refund.confirmed"
                                                    onChange={e => setDraftFilters({...draftFilters, action: e.target.value})}
                                                />
                                            </FormGroup>
                                        </Col>
                                        <Col md="3">
                                            <FormGroup>
                                                <Label>Target Type</Label>
                                                <Input
                                                    bsSize="sm"
                                                    value={draftFilters.target_type}
                                                    placeholder="e.g. refund"
                                                    onChange={e => setDraftFilters({...draftFilters, target_type: e.target.value})}
                                                />
                                            </FormGroup>
                                        </Col>
                                        <Col md="3">
                                            <FormGroup>
                                                <Label>Target ID</Label>
                                                <Input
                                                    bsSize="sm"
                                                    value={draftFilters.target_id}
                                                    onChange={e => setDraftFilters({...draftFilters, target_id: e.target.value})}
                                                />
                                            </FormGroup>
                                        </Col>
                                        <Col md="3">
                                            <FormGroup>
                                                <Label>Actor User ID</Label>
                                                <Input
                                                    bsSize="sm"
                                                    value={draftFilters.actor_id}
                                                    onChange={e => setDraftFilters({...draftFilters, actor_id: e.target.value})}
                                                />
                                            </FormGroup>
                                        </Col>
                                    </Row>

                                    <div className="mb-3">
                                        <Button color="primary" size="sm" onClick={applyFilters}>Apply</Button>{" "}
                                        <Button color="secondary" size="sm" onClick={clearFilters}>Clear</Button>
                                    </div>

                                    {isLoading ? (
                                        <div className="text-center my-4"><ProgressView/></div>
                                    ) : rows.length === 0 ? (
                                        <p className="text-center my-4">No audit log entries.</p>
                                    ) : (
                                        <Table className="table table-striped table-bordered">
                                            <Thead>
                                                <Tr>
                                                    <Th>Created</Th>
                                                    <Th>Action</Th>
                                                    <Th>Target</Th>
                                                    <Th>Actor</Th>
                                                    <Th>Changes</Th>
                                                </Tr>
                                            </Thead>
                                            <Tbody>
                                                {rows.map((r) => (
                                                    <Tr key={r.id}>
                                                        <Td><small>{formatDate(r.created_at)}</small></Td>
                                                        <Td><code>{r.action || "—"}</code></Td>
                                                        <Td>
                                                            <div><small className="text-muted">{r.target_type || "—"}</small></div>
                                                            <div><small>{r.target_id ? String(r.target_id).slice(0, 12) : "—"}</small></div>
                                                        </Td>
                                                        <Td>
                                                            <div><small>{r.actor_user_id ? String(r.actor_user_id).slice(0, 12) : "system"}</small></div>
                                                            {r.actor_type && <div><small className="text-muted">{r.actor_type}</small></div>}
                                                        </Td>
                                                        <Td>
                                                            {(r.before_json || r.after_json || r.changes_json) ? (
                                                                <details>
                                                                    <summary>view</summary>
                                                                    {r.changes_json && (
                                                                        <div>
                                                                            <small className="text-muted">changes</small>
                                                                            <pre style={{fontSize: "0.75rem", maxWidth: 360, whiteSpace: "pre-wrap"}}>{r.changes_json}</pre>
                                                                        </div>
                                                                    )}
                                                                    {r.before_json && (
                                                                        <div>
                                                                            <small className="text-muted">before</small>
                                                                            <pre style={{fontSize: "0.75rem", maxWidth: 360, whiteSpace: "pre-wrap"}}>{r.before_json}</pre>
                                                                        </div>
                                                                    )}
                                                                    {r.after_json && (
                                                                        <div>
                                                                            <small className="text-muted">after</small>
                                                                            <pre style={{fontSize: "0.75rem", maxWidth: 360, whiteSpace: "pre-wrap"}}>{r.after_json}</pre>
                                                                        </div>
                                                                    )}
                                                                </details>
                                                            ) : "—"}
                                                        </Td>
                                                    </Tr>
                                                ))}
                                            </Tbody>
                                        </Table>
                                    )}

                                    {totalPages > 1 && (
                                        <div className="d-flex justify-content-between align-items-center mt-3">
                                            <small>{totalRows} entries total</small>
                                            <div>
                                                <Button color="secondary" size="sm" disabled={page <= 1}
                                                        onClick={() => setPage(p => p - 1)}>Prev</Button>
                                                <span className="mx-2">Page {page} / {totalPages}</span>
                                                <Button color="secondary" size="sm" disabled={page >= totalPages}
                                                        onClick={() => setPage(p => p + 1)}>Next</Button>
                                            </div>
                                        </div>
                                    )}

                                </CardBody>
                            </Card>
                        </Col>
                    </Row>
                </Container>
            </div>
        </React.Fragment>
    )
}

export default AuditLogs
